"use client"

import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import {
  Sheet,
  SheetContentNoOverlay,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { useMediaQuery } from "@/hooks/useMediaQuery"
import { createContext, PropsWithChildren, useContext, useState } from "react"

type Props = PropsWithChildren<{
  title: string
}>

const SupportContext = createContext({ close: () => {} })
SupportContext.displayName = "SupportContext"

export function useCloseSupport() {
  const context = useContext(SupportContext)
  if (context === undefined) {
    throw new Error(
      "useCloseSupport must be used within <SupportSheetDrawer />"
    )
  }
  return context
}

export default function SupportSheetDrawer({ title, children }: Props) {
  const [open, setOpen] = useState(false)
  const isDesktop = useMediaQuery("(min-width: 640px)")

  if (isDesktop) {
    return (
      <SupportContext.Provider value={{ close: () => setOpen(false) }}>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetDescription className="sr-only">
            Support dialog
          </SheetDescription>
          <SheetTrigger asChild>
            <Button>Support</Button>
          </SheetTrigger>
          <SheetContentNoOverlay
            onOpenAutoFocus={(event) => event.preventDefault()}
            className="overflow-auto sm:max-h-screen"
          >
            <SheetHeader className="mb-2">
              <SheetTitle className="font-header text-foreground text-2xl">
                {title}
              </SheetTitle>
            </SheetHeader>
            {children}
          </SheetContentNoOverlay>
        </Sheet>
      </SupportContext.Provider>
    )
  }

  return (
    <SupportContext.Provider value={{ close: () => setOpen(false) }}>
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerDescription className="sr-only">
          Support dialog
        </DrawerDescription>
        <DrawerTrigger asChild>
          <Button>Support</Button>
        </DrawerTrigger>
        <DrawerContent className="px-2">
          <DrawerHeader className="px-4 pt-4 pb-2">
            <DrawerTitle className="font-header text-foreground text-xl text-left">
              {title}
            </DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-2">{children}</div>
        </DrawerContent>
      </Drawer>
    </SupportContext.Provider>
  )
}
